
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ImageUpload } from "@/components/shared/ImageUpload";

interface ProfilePhotoUploadProps {
  currentPhotoUrl?: string | null;
  onUpload: (url: string) => void;
}

export const ProfilePhotoUpload = ({ currentPhotoUrl, onUpload }: ProfilePhotoUploadProps) => {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null | undefined>(currentPhotoUrl);

  const handleProfilePhotoUpload = async (urls: string[]) => {
    if (urls.length === 0) return;
    
    try {
      setIsUploading(true);
      const newProfilePhotoUrl = urls[0];
      
      // Update user profile in database
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ profile_photo_url: newProfilePhotoUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setPhotoUrl(newProfilePhotoUrl);
      onUpload(newProfilePhotoUrl);
      
      toast({
        title: "Success",
        description: "Profile photo updated successfully",
      });
    } catch (error) {
      console.error("Error updating profile photo:", error);
      toast({
        title: "Error",
        description: "Failed to update profile photo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <Avatar className="h-20 w-20">
        <AvatarImage src={photoUrl || undefined} />
        <AvatarFallback>
          <User className="h-8 w-8" />
        </AvatarFallback>
      </Avatar>
      <div className="space-y-2">
        <ImageUpload
          bucketName="profile-photos"
          onUploadComplete={handleProfilePhotoUpload}
          maxFileSize={2}
          allowMultiple={false}
          maxWidth={400}
          maxHeight={400}
          buttonText="Upload Photo"
          filePrefix="profile-"
        />
        <p className="text-sm text-muted-foreground">
          Recommended: Square image, at least 400x400px
        </p>
      </div>
    </div>
  );
};
